import React, { useCallback, useEffect, useState } from 'react';
import { View } from 'react-native';
import {
  Button,
  Input,
  InputFeedback,
  KeyboardScreenContainer,
  Select,
  Text,
} from '~/components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { secondStepSchema } from '../../schemas';
import Banner from '~/scenes/MarisaTakesCare/components/Banner';
import Footer from '../Footer';
import type { StepComponentProps } from '~/components/StepsForm';
import { useTakesCareFormStore } from '~/stores/marisaTakesCareForm';
import type { SecondStepFormData } from '~/@types/MarisaTakesCare/form';
import { isMaxAgeValid } from '~/utils/date';
import { isWeb } from '~/utils/device';
import { LocalStorage } from '~/storage/LocalStorage';
import { ANONYMOUS_USER } from '~/storage/constants';
import type { AnonymousUserState } from '~/storage/types';
import { useSalesFunnel } from '~/stores';

const SecondStep: React.FC<StepComponentProps> = ({ onContinue }) => {
  const {
    functions: { setSecondStepData },
  } = useTakesCareFormStore();
  const { setInfos } = useSalesFunnel();

  const [maxAgeError, setMaxAgeError] = useState('');
  const [isCpfInputDisabled, setIsCpfInputDisabled] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<SecondStepFormData>({
    resolver: yupResolver(secondStepSchema),
  });

  const onSubmit = useCallback(
    (data: SecondStepFormData) => {
      setSecondStepData(data);
      setInfos({
        documentNumber: data.cpf,
        birthDate: data.birthDate,
        gender: data.gender,
        maritalStatus: data.maritalStatus,
      });
      onContinue?.();
    },
    [onContinue, setInfos, setSecondStepData]
  );

  const verifyMaxAge = (date?: string) => {
    if (date) {
      const maxAgeValid = isMaxAgeValid(date, 70);
      if (!maxAgeValid) {
        setMaxAgeError(
          'Clientes com idade superior a 70 anos não terão direito à cobertura de CheckUp'
        );
      } else {
        setMaxAgeError('');
      }
    }
  };

  useEffect(() => {
    async function verifyAnonymousUser() {
      const anonymousUserStorage = await LocalStorage.get<AnonymousUserState>(
        ANONYMOUS_USER
      );

      if (anonymousUserStorage && !anonymousUserStorage?.isNotInsured) {
        setValue('cpf', anonymousUserStorage.document);
      }

      setIsCpfInputDisabled(
        !!anonymousUserStorage && !anonymousUserStorage.isNotInsured && isWeb
      );
    }

    verifyAnonymousUser();
  }, [setValue]);

  return (
    <KeyboardScreenContainer
      footer={
        <View className="flex xl:hidden">
          <Footer onSubmit={handleSubmit(onSubmit)} />
        </View>
      }
    >
      <View className="flex-1 container mx-auto xl:flex-row xl:pt-12 xl:pb-24">
        <Banner showButton={false} />

        <View className="mt-8 xl:mt-0 px-6 flex-1 xl:ml-24">
          <Text className="text-xl mb-4 text-gray-600">Dados pessoais</Text>

          <Text className="text-gray-400 text-sm mb-8">
            Nos informe os dados do segurado:
          </Text>

          <Input
            control={control}
            label="Qual o CPF do segurado?"
            name="cpf"
            placeholder="Insira os números do CPF sem pontos"
            keyboardType="number-pad"
            error={errors.cpf?.message}
            maskConfig={{ type: 'cpf' }}
            disabled={isCpfInputDisabled}
          />

          <Input
            control={control}
            label="Data de nascimento"
            name="birthDate"
            placeholder="XX/XX/XXXX"
            keyboardType="number-pad"
            error={errors.birthDate?.message}
            maskConfig={{ type: 'custom', mask: '99/99/9999' }}
            onChangeText={(text) => {
              if (text && text.length === 10) {
                verifyMaxAge(text);
              }
            }}
          />

          <InputFeedback
            type="warn"
            message={maxAgeError}
            className={!errors.birthDate?.message ? 'mt-[-16px]' : ''}
          />

          <Select
            control={control}
            label="Gênero"
            name="gender"
            placeholder="Selecione o seu gênero"
            error={errors.gender?.message}
            items={[
              { label: 'Masculino', value: 'male' },
              { label: 'Feminino', value: 'female' },
              { label: 'Não informado', value: 'notInformed' },
            ]}
          />

          <Select
            control={control}
            label="Estado civil do segurado:"
            name="maritalStatus"
            placeholder="Selecione o estado civil"
            error={errors.maritalStatus?.message}
            items={[
              { label: 'Casado(a)', value: 'married' },
              { label: 'Solteiro(a)', value: 'single' },
              { label: 'Viúvo(a)', value: 'widower' },
              { label: 'Divorciado(a)', value: 'divorced' },
            ]}
          />

          {isWeb && (
            <View className="flex-1 justify-end hidden xl:flex">
              <Button className="w-ful" onPress={handleSubmit(onSubmit)}>
                CONTINUAR
              </Button>
            </View>
          )}
        </View>
      </View>
    </KeyboardScreenContainer>
  );
};

export default SecondStep;
